import React, { useState, useCallback, useEffect, useRef } from "react";
import { Select, Spin } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import { debounce } from "@mui/material";
import api from "../../utils/axios";
import googleLogo from "../../assets/google_on_white_hdpi.png";
import { toast } from "react-toastify";
import useLang from "../../hooks/useLang";

type Address = {
	id: number;
	address: string;
	sectionId?: number;
	latitude?: string;
	longitude?: string;
	placeId?: string;
};

type Props = {
	onAddressChange: (address: {
		lat: string;
		lng: string;
		label: string;
	}) => void;
	country: string;
	selectedAddress: any;
	city: string | number | undefined;
	disabled?: boolean;
	placeholder?: string;
	style?: React.CSSProperties;
	status?: "error" | "warning" | "";
	className?: string;
	id?: string;
};

const { Option } = Select;

const AddressSelect: React.FC<Props> = ({
	onAddressChange,
	country,
	selectedAddress,
	city,
	disabled,
	placeholder,
	style,
	status,
	className,
	id,
}) => {
	const [results, setResults] = useState<Address[]>([]);
	const [loading, setLoading] = useState(false);
	// Whether a search has answered for the current text — the difference between "nothing found"
	// and "nothing searched yet", which the dropdown words differently.
	const [searched, setSearched] = useState(false);
	// Keystrokes can overtake each other; only the newest search may fill the list.
	const requestRef = useRef(0);
	const { strings } = useLang("addressSelect");

	useEffect(() => {
		setResults([]);
		setSearched(false);
	}, [country, city]);

	const searchAddresses = useCallback(
		debounce(async (searchTerm: string) => {
			const requestId = ++requestRef.current;
			if (!searchTerm.trim()) {
				setResults([]);
				setSearched(false);
				setLoading(false);
				return;
			}

			try {
				// As params, so a "#" or "&" typed into the street name is sent rather than cutting
				// the query string short.
				const { data } = await api.get<Address[]>("Configuration", {
					params: { address: searchTerm, country, cityId: city },
				});
				if (requestId !== requestRef.current) return;
				// Replaced even when empty. Keeping the previous list made an earlier search's
				// streets look like matches for the new text.
				setResults(data ?? []);
				setSearched(true);
			} catch (error) {
				if (requestId !== requestRef.current) return;
				console.error("Error fetching addresses:", error);
				setResults([]);
				setSearched(true);
			} finally {
				if (requestId === requestRef.current) setLoading(false);
			}
		}, 300),
		[country, city]
	);

	const handleSearch = (value: string) => {
		if (!country || !city) {
			toast.error(
				"Ju lutem selektoni Shtetin dhe Qytetin per te vazhduar me adresen!"
			);
			return;
		}
		// Set before the debounce, so the dropdown says "searching" rather than repeating the
		// previous search's answer while the user is still typing.
		setLoading(Boolean(value.trim()));
		searchAddresses(value);
	};

	const handleSelect = async (selectedId: number) => {
		const selectedAddress = results.find((item) => item.id === selectedId);
		if (selectedAddress) {
			if (selectedAddress.placeId) {
				const latLng = await selectLngLatFromPlaceId(
					selectedAddress.placeId
				);
				if (latLng) {
					onAddressChange({
						lat: latLng.latitude || "",
						lng: latLng.longitude || "",
						label: selectedAddress.address,
					});
				}
			} else {
				onAddressChange({
					lat: selectedAddress.latitude || "",
					lng: selectedAddress.longitude || "",
					label: selectedAddress.address,
				});
			}
		}
	};

	const selectLngLatFromPlaceId = async (placeId: string) => {
		try {
			const result = await api.get(
				`Configuration/select-address-details?placeId=${placeId}`
			);
			const response = result.data;
			if (result.status === 200) {
				return response;
			}
		} catch (error) {
			console.log(error);
		}
	};

	const renderDropdownMessage = () => {
		if (loading) {
			return (
				<div className="flex items-center gap-2 px-1 py-1 text-gray-500">
					<Spin size="small" />
					{strings?.searching}
				</div>
			);
		}
		return (
			<div className="px-1 py-1 text-gray-500">
				{searched ? strings?.noResults : strings?.typeToSearch}
			</div>
		);
	};

	return (
		<Select
			id={id}
			showSearch
			placeholder={placeholder ?? "Search address"}
			notFoundContent={renderDropdownMessage()}
			filterOption={false}
			onSearch={handleSearch}
			onSelect={handleSelect}
			value={selectedAddress}
			disabled={disabled}
			style={style}
			status={status}
			// The field shows the plain address once picked, not the option's icon and layout.
			optionLabelProp="label"
			className={`registerInput ${className ?? ""}`}
			dropdownRender={(menu) => (
				<>
					{menu}
					{results.length > 0 && (
						<div
							style={{
								textAlign: "right",
								padding: "4px 8px",
								justifyContent: "flex-end",
							}}
						>
							<img
								src={googleLogo}
								alt="Powered by Google"
								style={{ height: 14 }}
							/>
						</div>
					)}
				</>
			)}
		>
			{results.map((item) => (
				<Option key={item.id} value={item.id} label={item.address}>
					{/* Wraps instead of truncating: two streets can share their first 40
					    characters, and the part that differs is at the end. */}
					<div className="flex items-start gap-2 whitespace-normal">
						<EnvironmentOutlined className="mt-1 text-gray-400" />
						<span>{item.address}</span>
					</div>
				</Option>
			))}
		</Select>
	);
};

export default AddressSelect;
